import AjaxApi from 'utils/ajaxApi';
import UserDetails from 'types/userDetails';

class DataManager {
    private readonly _ajaxApi: AjaxApi;

    constructor(ajaxApi: AjaxApi) {
        this._ajaxApi = ajaxApi;
    }

    // Get list of master accounts the current authenticated
    // user has access to
    getAccountList(): Promise<MasterAccount[]> {
        return this._makeGetRequest('masteraccounts');
    }

    // Get list of available user roles
    getRoles(): Promise<UserRole[]> {
        return this._makeGetRequest('roles');
    }

    // Get list of users/accounts within the given master account
    async getUsers(accountId: string): Promise<User[]> {
        if (!accountId) {
            return Promise.resolve([]);
        }

        const users = await this._makeGetRequest<User[]>(`masteraccounts/${accountId}/users`);
        return (users || []).map(DataManager._cleanUser);
    }

    // Get the details of a master account
    async getMasterAccount(accountId: string): Promise<MasterAccount | null> {
        if (!accountId) {
            return Promise.resolve(null);
        }

        return await this._makeGetRequest<MasterAccount>(`masteraccounts/${accountId}`);
    }

    // Get info about an individual user
    // (Note: this doesn't currently return user's roles)
    async getUser(userId: string): Promise<User | null> {
        if (!userId) {
            return Promise.resolve(null);
        }

        const user = await this._makeGetRequest<User>(`users/${userId}`);
        return DataManager._cleanUser(user);
    }

    // Reset the given user's password
    resetUserPassword(userId: string | null, product: string): Promise<void> {
        if (!userId || !product) {
            return Promise.reject(null);
        }

        return this._makePostRequest(`users/${userId}/resetpassword`, {
            product: this._cleanProductName(product)
        });
    }

    // Unlock given user's account
    unlockUserAccount(userId: string | null): Promise<void> {
        if (!userId) {
            return Promise.reject(null);
        }

        return this._makePostRequest(`users/${userId}/unlock`);
    }

    // Add/invite user to have access to given account/product
    inviteUser(
        account: string | null,
        product: string | null,
        details: UserDetails | null,
        sendInvite: boolean | null): Promise<void> {
        if (!details || !account || !product) {
            return Promise.reject(null);
        }

        return this._makePostRequest('invite', {
            masterAccountId: account,
            userEmail: details.email,
            firstName: details.firstName,
            lastName: details.lastName,
            roleIds: details.roleIds,
            product: this._cleanProductName(product),
            sendEmail: sendInvite === null ? true : sendInvite
        });
    }

    // Edit the given user's details
    editUser(userId: string | null, masterAccountId: string, details: UserDetails | null): Promise<void> {
        if (!details || !userId) {
            return Promise.reject(null);
        }

        return this._makePutRequest(`users/`, {
            userId,
            firstName: details.firstName,
            lastName: details.lastName,
            roles: details.roleIds,
            masterAccountId
        });
    }

    deleteUser(masterAccountId: string, userId: string | null): Promise<void> {
        if (!userId) {
            return Promise.reject(null);
        }

        return this._makePostRequest(`masteraccounts/${masterAccountId}/users/${userId}/delete`);
    }

    deleteInvite(inviteToken: string): Promise<void> {
        if (!inviteToken) {
            return Promise.reject(null);
        }

        return this._makeDeleteRequest(`invites/${inviteToken}`);
    }

    private static _cleanUser(user: User): User {
        if (!user) { return user; }
        user.displayName = user.displayName || '';
        user.userName = user.userName || '';
        user.roles = user.roles || [];
        return user;
    }

    private static _buildUrl(route: string): string {
        return `${window.appConfig.serviceUrl}/${route}`;
    }

    private _cleanProductName(product: string): string {
        return product.toLowerCase().replace(/[^A-Za-z]/g, '');
    }

    private async _makeGetRequest<T>(route: string): Promise<T> {
        return this._makeRequest(async () => await this._ajaxApi.get<T>(DataManager._buildUrl(route)));
    }

    private async _makePostRequest<T>(route: string, body: object = {}): Promise<T> {
        return this._makeRequest(async () => await this._ajaxApi.post<T>(DataManager._buildUrl(route), body));
    }

    private async _makePutRequest<T>(route: string, body: object = {}): Promise<T> {
        return this._makeRequest(async () => await this._ajaxApi.put<T>(DataManager._buildUrl(route), body));
    }

    private async _makeDeleteRequest<T>(route: string, body: object = {}): Promise<T> {
        return this._makeRequest(async () => await this._ajaxApi.delete<T>(DataManager._buildUrl(route), body));
    }

    private async _makeRequest<T>(callback: () => Promise<T>): Promise<T> {
        // if no api, can't do anything helpful
        if (!this._ajaxApi) {
            return Promise.reject(null);
        }

        try {
            return callback();
        } catch (e) {
            return Promise.reject(null);
        }
    }
}

export { DataManager };
export default DataManager;



// WEBPACK FOOTER //
// ./src/utils/dataManager.ts